import { EchartOption } from '../enums';
import { getWorkspace } from '../utils';

/**
 * Clear selection utility function for reports module
 * Resets all report-related state to their default values
 */
export const clearReportsSelection = (params: {
  // Required functions
  clearMyLoader: () => void;
  setAccordionInfo: (data: any[]) => void;
  setReportsData: (data: any) => void;
  setReportsRequest: (data: any) => void;
  setDateReportsData: (data: any) => void;
  setCheckboxReportsViewArr: (data: any[]) => void;
  setSortMenuReportsValuesData: (data: any[]) => void;
  setForceReportsShowLess: (data: any[]) => void;
  setForceReportsShowMore: (data: any[]) => void;
  setClearSelectionReports: (flag: boolean) => void;
  setTextStoreReports: (data: any[]) => void;
  setChartCalculationSelected: (option: EchartOption) => void;
  setHideCountsBtn: (flag: boolean) => void;
  setChartRatioSelected: (option: EchartOption) => void;
  setShowExplainerReports: (flag: boolean) => void;
  setExplainerReports: (data: string) => void;
  setExplainerReportsArray: (data: any[]) => void;

  // Common state setters
  setValueSearchBox: (data: any) => void;
  setClicksDataShowMore: (data: any[]) => void;
  setScrollDownData: (data: any[]) => void;
  setLastDataWithNoFilters: (data: any[]) => void;
  setViewsAllNonSelected: (data: any[]) => void;
  setUnfilteredToggleStatus: (status: any) => void;
  setBreakDownData: (data: any) => void;
  setUnfilterdata: (data: any) => void;
  setType: (type: EchartOption) => void;
  setTypeDateSelected: (type: EchartOption) => void;
  setFiltersData: (data: any) => void;
  setBreakDownSelected: (data: any) => void;
  setViewSelected: (data: any) => void;
  setChartSelected: (option: EchartOption) => void;
  setTabTypeSelected: (tab: number) => void;
  setViewsOpened: (data: any) => void;
  setClearClusterData: (flag: boolean) => void;

  // Optional data arrays
  viewData?: any[];

  // Optional mutation function for counter
  getCounterData?: {
    mutate: (payload: any) => void;
  };

  // Location for workspace ID
  location: string;
}) => {
  const {
    clearMyLoader,
    setAccordionInfo,
    setReportsData,
    setReportsRequest,
    setDateReportsData,
    setCheckboxReportsViewArr,
    setSortMenuReportsValuesData,
    setForceReportsShowLess,
    setForceReportsShowMore,
    setClearSelectionReports,
    setTextStoreReports,
    setChartCalculationSelected,
    setHideCountsBtn,
    setChartRatioSelected,
    setShowExplainerReports,
    setExplainerReports,
    setExplainerReportsArray,
    setValueSearchBox,
    setClicksDataShowMore,
    setScrollDownData,
    setLastDataWithNoFilters,
    setViewsAllNonSelected,
    setUnfilteredToggleStatus,
    setBreakDownData,
    setUnfilterdata,
    setType,
    setTypeDateSelected,
    setFiltersData,
    setBreakDownSelected,
    setViewSelected,
    setChartSelected,
    setTabTypeSelected,
    setViewsOpened,
    setClearClusterData,
    viewData,
    getCounterData,
    location
  } = params;

  // Clear loader
  clearMyLoader();

  // Reset viewData
  viewData?.forEach((element) => {
    element.values = [];
    element.sort = 'asc';
    element.expanded = false;
  });

  // Clear reports-specific state
  setAccordionInfo([]);
  setReportsData(null);
  setReportsRequest(null);
  setDateReportsData(undefined);
  localStorage.removeItem('handleCheck');
  setCheckboxReportsViewArr([]);
  setSortMenuReportsValuesData([]);
  setForceReportsShowLess([]);
  setForceReportsShowMore([]);
  setClearSelectionReports(true);
  setTextStoreReports([]);
  setChartCalculationSelected(EchartOption.COUNT);
  setHideCountsBtn(false);
  setChartRatioSelected(EchartOption.RATIO_SUM);
  setShowExplainerReports(false);
  setExplainerReports('');
  setExplainerReportsArray([]);

  // Clear common state
  setValueSearchBox(null);
  setClicksDataShowMore([]);
  setScrollDownData([]);
  setLastDataWithNoFilters([]);
  setViewsAllNonSelected([]);
  setUnfilteredToggleStatus(undefined);
  setBreakDownData(null);
  setUnfilterdata(null);
  setType(EchartOption.COUNT);
  setTypeDateSelected(EchartOption.DAY_DATE);
  localStorage.removeItem('type-date-selected');
  setFiltersData(null);
  setBreakDownSelected(null);
  setViewSelected(null);
  setChartSelected(EchartOption.GRID);
  setTabTypeSelected(0);
  setViewsOpened(null);
  setClearClusterData(true);

  // Fetch fresh counter data
  if (getCounterData) {
    const payload = {
      workspaceid: getWorkspace(location)
    };
    getCounterData.mutate(payload);
  }
};

/**
 * Clear selection utility function for cluster/signals module
 */
export const clearClusterSelection = (params: {
  setDataSignals: (data: any[]) => void;
  setAccordionInfoCluster: (data: any[]) => void;
  setDateSignalsData: (data: any) => void;
  setCheckboxSignalsViewArr: (data: any[]) => void;
  setSortMenuSignalsValuesData: (data: any[]) => void;
  setForceSignalsShowLess: (data: any[]) => void;
  setForceSignalsShowMore: (data: any[]) => void;
  setClearSelectionCohorts: (flag: boolean) => void;
  setValuesSignalsExcluded: (data: any[]) => void;
  setIsReRunButtonDisabled: (flag: boolean) => void;
  setValuesPayloadExcluded: (data: any[]) => void;
  setPayloadAIExplainerSignals: (data: any) => void;
  setExplainerSignals: (data: string) => void;
  setExplainerSignalsArray: (data: any[]) => void;

  viewClusterData?: any[];
}) => {
  const {
    setDataSignals,
    setAccordionInfoCluster,
    setDateSignalsData,
    setCheckboxSignalsViewArr,
    setSortMenuSignalsValuesData,
    setForceSignalsShowLess,
    setForceSignalsShowMore,
    setClearSelectionCohorts,
    setValuesSignalsExcluded,
    setIsReRunButtonDisabled,
    setValuesPayloadExcluded,
    setPayloadAIExplainerSignals,
    setExplainerSignals,
    setExplainerSignalsArray,
    viewClusterData
  } = params;

  setDataSignals([]);

  viewClusterData?.forEach((element) => {
    element.values = [];
    element.sort = 'asc';
    element.expanded = false;
  });

  setAccordionInfoCluster([]);
  setDateSignalsData(undefined);
  setCheckboxSignalsViewArr([]);
  setSortMenuSignalsValuesData([]);
  setForceSignalsShowLess([]);
  setForceSignalsShowMore([]);
  setClearSelectionCohorts(true);
  setValuesSignalsExcluded([]);
  setIsReRunButtonDisabled(true);
  setValuesPayloadExcluded([]);
  setPayloadAIExplainerSignals(null);
  setExplainerSignals('');
  setExplainerSignalsArray([]);
};

/**
 * Clear selection utility function for correlation module
 */
export const clearCorrelationSelection = (params: {
  setViewCorrelationData?: (data: any) => void;
  setRowsAgGrid: (data: any[]) => void;
  setAccordionInfoCorrelation: (data: any[]) => void;
  setClearSelectionCorrelation: (flag: boolean) => void;
  setCheckboxCorrelationViewArr: (data: any[]) => void;
  setSortMenuCorrelationValuesData: (data: any[]) => void;
  setForceCorrelationShowLess: (data: any[]) => void;
  setForceCorrelationShowMore: (data: any[]) => void;
  setPayloadAIExplainerCorrelations: (data: any) => void;
  setExplainerCorrelations: (data: string) => void;
  setExplainerCorrelationsArray: (data: any[]) => void;

  viewCorrelationData?: any[];
}) => {
  const {
    setViewCorrelationData,
    setRowsAgGrid,
    setAccordionInfoCorrelation,
    setClearSelectionCorrelation,
    setCheckboxCorrelationViewArr,
    setSortMenuCorrelationValuesData,
    setForceCorrelationShowLess,
    setForceCorrelationShowMore,
    setPayloadAIExplainerCorrelations,
    setExplainerCorrelations,
    setExplainerCorrelationsArray,
    viewCorrelationData
  } = params;

  viewCorrelationData?.forEach((element) => {
    element.values = [];
    element.sort = 'asc';
    element.expanded = false;
  });

  setViewCorrelationData && setViewCorrelationData(viewCorrelationData);
  setRowsAgGrid([]);
  setAccordionInfoCorrelation([]);
  setClearSelectionCorrelation(true);
  setCheckboxCorrelationViewArr([]);
  setSortMenuCorrelationValuesData([]);
  setForceCorrelationShowLess([]);
  setForceCorrelationShowMore([]);
  setPayloadAIExplainerCorrelations(null);
  setExplainerCorrelations('');
  setExplainerCorrelationsArray([]);
};
